import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #notificationTimer = null;

  /**
   * Whether the most recent cart action (add or quantity change) involved a promo item.
   * Set to true only when the item triggering the update has .product-promo-badges .promo-badge
   * in the cart drawer DOM (quantity change) or via deal-count delta in sections HTML (add to cart).
   * @type {boolean}
   */
  #pendingUpdateIsPromo = false;

  /** @type {number | null} */
  #alpTimeoutId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#resetNotificationAutoHide);
    this.addEventListener(DialogOpenEvent.eventName, this.#loadRecommendations);
    this.addEventListener(DialogOpenEvent.eventName, this.#hideNotificationsOnOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleAlpOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleAlpClose);
    document.addEventListener(ThemeEvents.cartUpdate, this.#loadRecommendations);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleTabSync);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleNotificationAutoHide);
    // Pre-flight: capture promo state of the cart row BEFORE the API call fires
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.addEventListener('toggle', this.#updateStickyState, { capture: true });
    this.addEventListener('click', this.#handleTabClick);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('cart') === 'open') {
      this.#openFromUrl();
      urlParams.delete('cart');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetNotificationAutoHide);
    this.removeEventListener(DialogOpenEvent.eventName, this.#loadRecommendations);
    this.removeEventListener(DialogOpenEvent.eventName, this.#hideNotificationsOnOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleAlpOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleAlpClose);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#loadRecommendations);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleTabSync);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleNotificationAutoHide);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.removeEventListener('toggle', this.#updateStickyState, { capture: true });
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Fires on ThemeEvents.quantitySelectorUpdate — BEFORE the cart API call.
   * Checks if the cart item row being changed has promo badges and caches the result.
   * @param {Event} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    const target = event.target;
    if (!target) return;

    // Only care about quantity selectors inside this cart drawer
    if (!this.contains(/** @type {Node} */ (target))) {
      this.#pendingUpdateIsPromo = false;
      return;
    }

    // Walk up to the cart item row <tr ref="cartItemRows[]">
    const cartItemRow = /** @type {Element} */ (target).closest('tr');
    if (!cartItemRow) {
      this.#pendingUpdateIsPromo = false;
      return;
    }

    // Row is promo if it has at least one rendered promo badge
    this.#pendingUpdateIsPromo = cartItemRow.querySelector('.product-promo-badges .promo-badge') !== null;
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * Sets #pendingUpdateIsPromo by comparing new sections HTML deal counts vs current DOM counts.
   * @param {CustomEvent} event
   */
  #handleCartAdd = (event) => {
    // Resolve promo state for the added product before the drawer opens
    this.#pendingUpdateIsPromo = this.#addedProductIsPromo(event);

    if (this.hasAttribute('auto-open') && !event.detail.data?.didError) {
      this.showDialog();
      // DialogOpenEvent hides notifications first (#hideNotificationsOnOpen).
      // #handleNotificationAutoHide is already registered on ThemeEvents.cartUpdate,
      // but the drawer open event resets the flag — fire it again after settling.
      setTimeout(() => this.#handleNotificationAutoHide(), 300);
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Determines whether the product added via CartAddEvent is a promo item.
   *
   * Compares the new sections HTML deal-item counts against the current live DOM.
   * If any deal banner gained items (or is brand new), the added product is promo.
   * Falls back to checking the source product-form element for promo badges.
   *
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #addedProductIsPromo(event) {
    const sections = event.detail?.data?.sections;

    if (sections) {
      for (const html of Object.values(sections)) {
        const newDoc = new DOMParser().parseFromString(html, 'text/html');
        const newBanners = newDoc.querySelectorAll('.cart-deal-notification[data-items-count]');

        for (const newBanner of newBanners) {
          const newCount = parseInt(newBanner.dataset.itemsCount || '0', 10);
          const bannerId = newBanner.id; // e.g. "banner-<metaobject-id>"
          const existingBanner = bannerId ? this.querySelector(`#${CSS.escape(bannerId)}`) : null;
          const existingCount = parseInt(existingBanner?.dataset.itemsCount || '0', 10);

          if (newCount > existingCount) {
            // This promo deal gained items — the added product is promo
            return true;
          }
        }

        // Brand new promo deal appeared where there was none before
        if (newBanners.length > 0 && this.querySelectorAll('.cart-deal-notification').length === 0) {
          return true;
        }
      }
    }

    // Fallback: check the source product-form / quick-add element for promo badges
    const sourceId = event.detail?.sourceId;
    if (sourceId) {
      const sourceEl = document.getElementById(sourceId);
      if (sourceEl) {
        if (sourceEl.querySelector('.product-promo-badges .promo-badge')) return true;
        const productAncestor = sourceEl.closest('[data-product-id], product-card, .product');
        if (productAncestor?.querySelector('.product-promo-badges .promo-badge')) return true;
      }
    }

    return false;
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #openFromUrl() {
    if (this.refs?.dialog) {
      this.open();
    } else {
      requestAnimationFrame(() => this.#openFromUrl());
    }
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const isYmalOpen = summary.querySelector('.ymal-accordion details[open]') !== null;
    const threshold = isYmalOpen ? 0.5 : 0.65;

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > threshold ? 'false' : 'true');

    if (event && event.type === 'toggle') {
      setTimeout(() => {
        this.#updateStickyState();
      }, 500);
    }
  };

  #handleAlpOpen = () => {
    if (window.innerWidth >= 750) {
      if (this.#alpTimeoutId) clearTimeout(this.#alpTimeoutId);
      this.#alpTimeoutId = setTimeout(() => {
        document.body.classList.add('alp-active');
        this.#alpTimeoutId = null;
      }, 400);
    }
  };

  #handleAlpClose = () => {
    if (this.#alpTimeoutId) {
      clearTimeout(this.#alpTimeoutId);
      this.#alpTimeoutId = null;
    }
    document.body.classList.remove('alp-active');
  };

  #loadRecommendations = (event) => {
    // If triggered by a cart update, we might have a new top product
    let productId = this.dataset.productId;
    let newProductId = productId;

    if (event?.detail?.resource?.items?.length > 0) {
      newProductId = event.detail.resource.items[0].product_id;
    } else if (event?.detail?.data?.productId) {
      newProductId = event.detail.data.productId;
    } else {
      const firstRow = document.querySelector('cart-items-component [data-product-id]');
      if (firstRow) {
        newProductId = firstRow.dataset.productId;
      }
    }

    const containers = document.querySelectorAll('[id^="cart-recommendations-container"]');
    let hasContent = false;
    containers.forEach(container => {
      if (container.querySelector('.you-may-also-like')) {
        hasContent = true;
      }
    });

    if (newProductId && newProductId.toString() === productId?.toString() && hasContent) {
      return;
    }

    if (!newProductId) return;
    this.dataset.productId = newProductId;

    fetch(`${window.Shopify.routes.root}recommendations/products?product_id=${newProductId}&section_id=you-may-also-like&limit=4`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // Search for our content wrapper in the response
        const newContent = doc.querySelector('.you-may-also-like-wrapper') ||
          doc.querySelector('#shopify-section-you-may-also-like') ||
          doc.querySelector('.you-may-also-like');

        if (newContent) {
          // Update all containers that might exist (Desktop and Mobile)
          const containers = document.querySelectorAll('[id^="cart-recommendations-container"]');
          containers.forEach(container => {
            container.innerHTML = newContent.innerHTML;
          });
          document.dispatchEvent(new CustomEvent('ymal:loaded'));
        }
      })
      .catch(e => console.error('Cart recommendations error:', e));
  };

  #handleTabClick = (e) => {
    const target = e.target.closest('.cart-drawer__heading');
    if (!target) return;

    e.preventDefault();
    const isDeals = target.classList.contains('cart-drawer__heading_deals');

    const dialog = this.refs.dialog;
    if (!dialog) return;

    // Update active state of headings
    dialog.querySelectorAll('.cart-drawer__heading').forEach(h => h.classList.remove('active'));
    target.classList.add('active');

    // Toggle content visibility
    const cartContent = dialog.querySelector('.cart-drawer__items--cart');
    const dealsContent = dialog.querySelector('.cart-drawer__items--deals');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (isDeals) {
      if (cartContent) cartContent.style.display = 'none';
      if (dealsContent) dealsContent.style.display = 'block';
    } else {
      if (cartContent) cartContent.style.display = 'block';
      if (dealsContent) dealsContent.style.display = 'none';
    }
  };

  #handleTabSync = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const dealsTab = dialog.querySelector('.cart-drawer__heading_deals');
    const cartTab = dialog.querySelector('#cart-drawer-heading');

    if (!dealsTab && cartTab) {
      cartTab.classList.add('active');
      const cartContent = dialog.querySelector('.cart-drawer__items--cart');
      if (cartContent) cartContent.style.display = 'block';
    }
  };

  // Called when cart drawer opens — always hide the notification wrapper immediately
  #hideNotificationsOnOpen = () => {
    if (this.#notificationTimer) clearTimeout(this.#notificationTimer);
    this.setAttribute('data-notifications-hidden', 'true');
  };

  /**
   * Called on ThemeEvents.cartUpdate (quantity change from cart OR add to cart).
   *
   * For quantity changes: #pendingUpdateIsPromo was already set by #handleQuantitySelectorUpdate
   *   which ran synchronously BEFORE the API call, reading the live cart item row DOM.
   *
   * For add to cart: #pendingUpdateIsPromo was set by #handleCartAdd → #addedProductIsPromo
   *   which compared new vs old deal-item counts from the CartAddEvent sections HTML.
   *
   * Only removes data-notifications-hidden when the triggering item is promo.
   */
  #handleNotificationAutoHide = () => {
    if (this.#notificationTimer) clearTimeout(this.#notificationTimer);

    if (!this.#pendingUpdateIsPromo) {
      // The item that triggered this update is NOT promo — keep notification hidden
      this.setAttribute('data-notifications-hidden', 'true');
      return;
    }

    // Promo item triggered the update — reveal the notification
    this.removeAttribute('data-notifications-hidden');

    // Auto-hide after 5 seconds
    this.#notificationTimer = setTimeout(() => {
      const { dialog } = this.refs;
      if (!dialog || !dialog.open) return;

      this.setAttribute('data-notifications-hidden', 'true');
    }, 5000);
  };

  #resetNotificationAutoHide = () => {
    if (this.#notificationTimer) clearTimeout(this.#notificationTimer);
    // Keep notifications hidden when drawer closes (reset for next open)
    this.setAttribute('data-notifications-hidden', 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
